<template>
  <div>
    <div
      v-if="holidayWorkList.length"
      class="table-responsive"
    >
      <span
        v-if="submitting || reminding || approving"
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th>No</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Holiday Name</th>
            <th>Reason</th>
            <th class="text-center">Half Day</th>
            <th>Status</th>
            <th v-if="shouldShowActions">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, key) in holidayWorkList"
            :key="item.id"
          >
            <td>
              {{ ++key }}
            </td>
            <td>{{ item.start_date }}</td>
            <td>{{ item.end_date }}</td>
            <td>{{ item.holiday_name }}</td>
            <td>{{ item.reason }}</td>
            <td class="text-center">
              <span v-if="item.half_day === 1 || item.half_day === true" class="badge bg-info">
                Yes
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <span
                class="badge"
                :class="statusBadgeClass(item)"
              >
                {{ getStatus(item) }}
              </span>
            </td>
            <td v-if="shouldShowActions">
              <template v-if="isSuperAdmin && item.status === 'pending'">
                <button
                  type="button"
                  class="btn btn-success btn-sm me-1"
                  :disabled="approving"
                  @click="approveRequest(item.id)"
                >
                  Approve
                </button>
                <button
                  type="button"
                  class="btn btn-danger btn-sm"
                  :disabled="approving"
                  @click="rejectRequest(item.id)"
                >
                  Reject
                </button>
              </template>
              <template v-else-if="isSuperAdmin && item.status === 'approved'">
                <button
                  type="button"
                  class="btn btn-danger btn-sm"
                  :disabled="approving"
                  @click="denyRequest(item.id)"
                >
                  Deny
                </button>
              </template>
              <template v-else-if="!isSuperAdmin && item.status === 'pending'">
                <button
                  type="button"
                  class="btn btn-warning btn-sm me-1"
                  :disabled="submitting"
                  @click="cancelRequest(item.id)"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-info btn-sm"
                  :disabled="reminding"
                  @click="remindRequest(item.id)"
                >
                  Remind
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p>No Holiday Work Request Existed</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "HolidayWorkListTable",
    props: {
        holidayWorkList: {
            type: Array,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        staffId: {
            type: Number,
            required: true,
        },
        isManager: {
            type: Boolean,
            required: true,
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        isSuperAdmin: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
            reminding: false,
            approving: false,
            currentActionId: null,
        };
    },
    computed: {
        shouldShowActions() {
            return !this.readOnly || this.isSuperAdmin;
        },
    },
    methods: {
        getStatus(item) {
            if (item.status === "approved") return "Approved";
            if (item.status === "rejected") return "Rejected";
            if (item.status === "pending") return "Pending";
            return item.status || "Submitted";
        },
        statusBadgeClass(item) {
            if (item.status === "approved") return "bg-success";
            if (item.status === "rejected") return "bg-danger";
            return "bg-info";
        },
        async cancelRequest(id) {
            try {
                if (confirm("Do you want to cancel this holiday work request?") === false) {
                    return;
                }
                this.submitting = true;
                await this.cancelHolidayWorkRequest({ id });
                alert("Holiday work request has been cancelled");
                this.$emit("get-list");
            } catch (error) {
                alert("An error occurred while cancelling the request");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        async remindRequest(id) {
            try {
                if (confirm("Do you want to send a reminder for this holiday work request?") === false) {
                    return;
                }
                this.reminding = true;
                await this.remindHolidayWorkRequest({ id });
                alert("Reminder has been sent");
                this.$emit("get-list");
            } catch (error) {
                alert("An error occurred while sending the reminder");
                console.log(error);
            } finally {
                this.reminding = false;
            }
        },
        async approveRequest(id) {
            if (!confirm("Approve this holiday work request?")) return;
            try {
                this.approving = true;
                this.currentActionId = id;
                await this.managerUpdateRequest({ id, status: "approved", type: "holiday_work" });
                this.$emit("get-list");
            } catch {
                alert("An error occurred while approving the request");
            } finally {
                this.approving = false;
                this.currentActionId = null;
            }
        },
        async rejectRequest(id) {
            if (!confirm("Reject this holiday work request?")) return;
            try {
                this.approving = true;
                this.currentActionId = id;
                await this.managerUpdateRequest({ id, status: "rejected", type: "holiday_work" });
                this.$emit("get-list");
            } catch {
                alert("An error occurred while rejecting the request");
            } finally {
                this.approving = false;
                this.currentActionId = null;
            }
        },
        async denyRequest(id) {
            if (!confirm("Deny this approved holiday work request? This will change the status to rejected.")) return;
            try {
                this.approving = true;
                this.currentActionId = id;
                await this.managerUpdateRequest({ id, status: "rejected", type: "holiday_work" });
                this.$emit("get-list");
            } catch {
                alert("An error occurred while denying the request");
            } finally {
                this.approving = false;
                this.currentActionId = null;
            }
        },
        ...mapActions({
            cancelHolidayWorkRequest: "vacation/cancelHolidayWorkRequest",
            remindHolidayWorkRequest: "vacation/remindHolidayWorkRequest",
            managerUpdateRequest: "vacation/managerUpdateRequest",
        }),
    },
};
</script>
